import { Component, createSignal, onMount, onCleanup, Show } from 'solid-js'
import styles from '~/components/search-replace/search-replace.module.scss'
import Search from '~/assets/icons/search.svg'
import Replace from '~/assets/icons/switch.svg'
import ArrowUp from '~/assets/icons/arrow-up.svg'
import ArrowDown from '~/assets/icons/arrow-down.svg'
import { highlightTranslation, translations, updateTranslation } from '~/services/current-document/translations'
import ConfirmationModal from '~/components/confirmation-modal/confirmation-modal'
import { translations as translationsDict } from '~/translations'
import { SearchReplaceProps, SearchResults } from '~/components/search-replace/search-replace.interfaces'
import { history } from '~/editor/history'
import Action from '~/editor/history/action'

const SearchReplace: Component<SearchReplaceProps> = ({ readonly = false }) => {
  const [showSearch, setShowSearch] = createSignal(false)
  const [showReplaceConfirmation, setReplaceConfirmation] = createSignal(false)
  let searchInputRef!: HTMLInputElement

  const toggleSearch = () => {
    setShowSearch(true)
    searchInputRef.focus()
  }

  const onKeydown = (event: KeyboardEvent) => {
    const chordKey = window.navigator.platform.includes('Mac') ? event.metaKey : event.ctrlKey
    if (chordKey && event.key === 'f') {
      event.preventDefault()
      toggleSearch()
    }
  }

  // Register event listener for Ctrl+F
  onMount(() => {
    document.addEventListener('keydown', e => onKeydown(e))
  })
  onCleanup(() => {
    document.removeEventListener('keydown', e => onKeydown(e))
  })

  // Keep track of the search terms
  const [fields, setFields] = createSignal({
    searchTerm: '',
    replaceTerm: ''
  })
  const onInput = (fieldKey: string) => (event: Event) => {
    const value = (event.target && 'value' in event.target) ? event.target.value : ''
    setFields(state => ({
      ...state,
      [fieldKey]: value
    }))
    if (fieldKey === 'searchTerm') {
      setMatchIndex(0)
      setOccurrenceIndex(0)
      highlightTerm()
    }
  }

  // Update the matched entries on searchTerm changes
  const [matchIndex, setMatchIndex] = createSignal(0)
  const [occurrenceIndex, setOccurrenceIndex] = createSignal(0)

  const matches = (): SearchResults => {
    const searchTerm = fields().searchTerm.toLowerCase()
    const results: SearchResults = []

    if (searchTerm !== '') {
      translations().forEach(entry => {
        const translatedText = entry.getLastVersion().translatedText.toLowerCase()
        const matches = translatedText.match(new RegExp(searchTerm, 'gi'))
        if (matches) {
          results.push({
            count: matches.length,
            translationEntry: entry
          })
        }
      })
    }

    return results
  }

  const totalOccurrences = () => {
    return matches().reduce((total, result) => total + result.count, 0)
  }

  const getCurrentOccurrenceGlobalIndex = () => {
    let globalIndex = 0
    const currentMatches = matches()

    for (let i = 0; i < matchIndex(); i++) {
      if (i < currentMatches.length) {
        globalIndex += currentMatches[i].count
      }
    }

    globalIndex += occurrenceIndex() + 1
    return globalIndex
  }

  const matchesLabel = () => {
    const total = totalOccurrences()
    const currentGlobalIndex = getCurrentOccurrenceGlobalIndex()
    const label = total > 0
      ? translationsDict().general.search.resultIndex(currentGlobalIndex.toString(), total.toString())
      : translationsDict().general.search.noResult
    return label
  }

  const replaceConfirmation = () => translationsDict().general.search.replaceConfirmation(totalOccurrences())

  // Step through matches by moving the matchIndex and occurrenceIndex
  const prevTerm = () => {
    const currentMatches = matches()
    if (currentMatches.length === 0) return

    let newMatchIndex = matchIndex()
    let newOccurrenceIndex = occurrenceIndex() - 1

    if (newOccurrenceIndex < 0) {
      newMatchIndex = newMatchIndex - 1
      if (newMatchIndex < 0) {
        newMatchIndex = currentMatches.length - 1
      }
      newOccurrenceIndex = currentMatches[newMatchIndex].count - 1
    }

    setMatchIndex(newMatchIndex)
    setOccurrenceIndex(newOccurrenceIndex)
    highlightTerm()
  }

  const nextTerm = () => {
    const currentMatches = matches()
    if (currentMatches.length === 0) return

    let newMatchIndex = matchIndex()
    let newOccurrenceIndex = occurrenceIndex() + 1

    if (newOccurrenceIndex >= currentMatches[newMatchIndex].count) {
      newMatchIndex = newMatchIndex + 1
      newOccurrenceIndex = 0
      if (newMatchIndex >= currentMatches.length) {
        newMatchIndex = 0
      }
    }

    setMatchIndex(newMatchIndex)
    setOccurrenceIndex(newOccurrenceIndex)
    highlightTerm()
  }

  // After replacing 1 occurrence
  // Update indices to point to the next valid occurrence
  const nextValidTerm = () => {
    const currentMatches = matches()
    if (currentMatches.length === 0) return

    let newMatchIndex = matchIndex()
    let newOccurrenceIndex = occurrenceIndex()

    // If current entry still has occurrences at or after current index, stay
    if (newOccurrenceIndex < currentMatches[newMatchIndex].count) {
      // Stay at current occurrence index since we just replaced one
      setMatchIndex(newMatchIndex)
      setOccurrenceIndex(newOccurrenceIndex)
    } else {
      // Move to next entry's first occurrence
      newMatchIndex = newMatchIndex + 1
      newOccurrenceIndex = 0
      if (newMatchIndex >= currentMatches.length) {
        newMatchIndex = 0
      }
      setMatchIndex(newMatchIndex)
      setOccurrenceIndex(newOccurrenceIndex)
    }

    highlightTerm()
  }

  // When the matchIndex changes, jump to corresponding entry
  const highlightTerm = () => {
    const currentMatches = matches()
    const currentIndex = matchIndex()
    if (currentIndex < currentMatches.length) {
      const resultEntry = currentMatches[currentIndex]
      highlightTranslation(resultEntry.translationEntry, { scrollView: 'sideview' })
    }
  }

  // Add navigation controls when search field is in focus
  const onSearchKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'ArrowDown') {
      nextTerm()
    }
    if (event.code === 'ArrowUp') {
      prevTerm()
    }
    if (event.code === 'Escape') {
      setShowSearch(false)
      setFields({ searchTerm: '', replaceTerm: '' })
    }
  }

  // Close search when search field loses focus
  // and search field is empty
  const onSearchBlur = () => {
    const { searchTerm } = fields()
    if (!searchTerm) {
      setShowSearch(false)
    }
  }

  // Show replace confirmation on Enter
  const onReplaceKeyDown = (event: KeyboardEvent) => {
    if (event.code === 'Enter') {
      (event.target as HTMLInputElement).blur()
      event.stopPropagation()
      onReplaceEntry()
    }
  }

  // Replace actions - replace one occurrence at a time
  const onReplaceEntry = () => {
    const { searchTerm, replaceTerm } = fields()
    const currentMatches = matches()
    const currentMatchIndex = matchIndex()
    const currentOccurrenceIndex = occurrenceIndex()

    if (currentMatchIndex < currentMatches.length) {
      const resultEntry = currentMatches[currentMatchIndex]
      const entry = resultEntry.translationEntry
      const currentText = entry.getLastVersion().translatedText

      // Find all matches and replace only the one at currentOccurrenceIndex
      const regex = new RegExp(searchTerm, 'gi')
      const matches = [...currentText.matchAll(regex)]

      if (currentOccurrenceIndex < matches.length) {
        const matchToReplace = matches[currentOccurrenceIndex]
        const beforeMatch = currentText.substring(0, matchToReplace.index!)
        const afterMatch = currentText.substring(matchToReplace.index! + matchToReplace[0].length)
        const updatedText = beforeMatch + replaceTerm + afterMatch

        updateTranslation(entry.id, {
          translatedText: updatedText
        })

        // Add the action to the history
        history.append(new Action({
          name: 'replaceTranslation',
          undo: () => updateTranslation(entry.id, {
            translatedText: currentText
          }),
          redo: () => updateTranslation(entry.id, {
            translatedText: updatedText
          })
        }))

        nextValidTerm()
      }
    }
  }

  const confirmReplaceAll = () => {
    setReplaceConfirmation(true)
  }

  const onReplaceAll = () => {
    const { searchTerm, replaceTerm } = fields()
    matches().forEach(resultEntry => {
      const entry = resultEntry.translationEntry
      const updatedText = entry.getLastVersion().translatedText.replaceAll(new RegExp(searchTerm, 'gi'), replaceTerm)
      updateTranslation(entry.id, {
        translatedText: updatedText
      })
    })
    setReplaceConfirmation(false)
  }

  return (
    <>
      <ConfirmationModal
        onConfirm={onReplaceAll}
        open={showReplaceConfirmation()}
        setOpen={setReplaceConfirmation}
        title={replaceConfirmation()}
        confirmIcon={Replace}
        confirmLabel={translationsDict().general.actions.replace}
      />

      <Show
        when={showSearch()}
        fallback={
          <div class={styles.searchReplace}>
            <button
              class={styles.field}
              onClick={toggleSearch}
            >
              <Search class={styles.icon} />
              <input
                class={styles.input}
                type='text'
                placeholder={`${translationsDict().general.actions.search}...`}
              />
            </button>
          </div>
        }
      >
        <div class={styles.searchReplace}>
          <div class={styles.fields}>
            <div class={styles.fieldRow}>
              <div class={styles.field}>
                <Search class={styles.icon} />
                <input
                  ref={searchInputRef}
                  class={styles.input}
                  type='text'
                  placeholder={`${translationsDict().general.actions.search}...`}
                  value={fields().searchTerm}
                  onInput={onInput('searchTerm')}
                  onKeyDown={onSearchKeyDown}
                  onBlur={onSearchBlur}
                  tabIndex={1}
                />
              </div>

              <button
                onClick={prevTerm}
                class={styles.button}
              >
                <ArrowUp class={styles.icon} />
              </button>

              <button
                onClick={nextTerm}
                class={styles.button}
              >
                <ArrowDown class={styles.icon} />
              </button>

              {fields().searchTerm.length > 0 && (
                <div class={styles.matchesCount}>
                  {matchesLabel()}
                </div>
              )}
            </div>
            <Show when={!readonly}>
              <div class={styles.fieldRow}>
                <div class={styles.field}>
                  <Replace class={styles.icon} />

                  <input
                    class={styles.input}
                    type='text'
                    placeholder={`${translationsDict().general.actions.replaceBy}...`}
                    value={fields().replaceTerm}
                    onInput={onInput('replaceTerm')}
                    onKeyDown={onReplaceKeyDown}
                    tabIndex={2}
                  />
                </div>

                <div class={styles.replaceControls}>
                  <button
                    onClick={onReplaceEntry}
                    class={styles.button}
                  >
                    <Replace class={styles.icon} />
                    <div class={styles.label}>Remplacer l'entrée</div>
                  </button>

                  <button
                    onClick={confirmReplaceAll}
                    class={styles.button}
                  >
                    <Replace class={styles.icon} />
                    <div class={styles.label}>Remplacer tout</div>
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </>
  )
}

export default SearchReplace